import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import type { ReactNode } from 'react'

import type { Language } from '@/bindings/Language'
import type { OrderStatus } from '@/bindings/OrderStatus'
import type { QueueOrder } from '@/bindings/QueueOrder'
import { Badge } from '@/components/badge'
import { Field, Select } from '@/components/field'
import { Card, Page } from '@/components/layout'
import { Pager } from '@/components/pager'
import { bestsellersQuery, ordersOfDateQuery } from '@/features/back-office/api'
import { ItemList, Loadable, ORDERS_OF_DATE_PATH, STATISTICS_PATH, SpanLabel } from '@/features/back-office/statistics-page'
import { ORDER_STATUSES, parseOrderStatus, whereLabel } from '@/features/ordering/order-rules'
import { StatusBadge } from '@/features/ordering/order-view'
import { minorUnits } from '@/lib/currencies'
import { useLanguage } from '@/lib/i18n/store'
import { useManagedBranch } from '@/features/back-office/scope'
import { useT } from '@/lib/i18n/translate'
import { formatPrice } from '@/lib/menu-format'
import { type StatisticsSearch, viewOf } from '@/features/back-office/statistics-params'
import { canManageBranch } from '@/lib/roles'
import { formatDate, formatTime, todayIn } from '@/lib/time'

const BESTSELLERS_ROUTE_ID = '/$restaurantSlug/$branchSlug/manage/statistics/bestsellers'
const ORDERS_ROUTE_ID = '/$restaurantSlug/$branchSlug/manage/statistics/orders'
const ORDER_PATH = '/$restaurantSlug/$branchSlug/manage/orders/$orderId'

/** Header with the back link to the overview, plus the admin gate both list pages share. */
function ListPage({
  title,
  back,
  children,
}: {
  title: (today: string) => string
  /** The overview view the page was opened from. */
  back: (today: string) => StatisticsSearch
  children: (main: Language, today: string) => ReactNode
}) {
  const t = useT()
  const { restaurantSlug, branchSlug, context, branch } = useManagedBranch()
  const today = todayIn(branch?.timezone)
  const header = (
    <div className="flex flex-wrap items-center gap-3">
      <h1 className="text-xl font-semibold">{title(today)}</h1>
      {branch && <span className="text-muted">{branch.name}</span>}
      {branch?.deactivated_at && <Badge tone="muted">{t('restaurants.deactivated')}</Badge>}
    </div>
  )
  return (
    <Page width="md" className="flex flex-col gap-4">
      <Link to={STATISTICS_PATH} params={{ restaurantSlug, branchSlug }} search={back(today)} className="text-accent self-start text-sm font-medium hover:underline">
        ← {t('stats.title')}
      </Link>
      {header}
      {canManageBranch(context.role) ? (
        children(context.settings.main_language, today)
      ) : (
        <p role="alert" className="text-muted text-sm">
          {t('stats.adminOnly')}
        </p>
      )}
    </Page>
  )
}

/** Every item sold in the span, per category, best-selling first (the PRD's "all items" link). */
export function BestsellersPage() {
  const t = useT()
  const { restaurantId, branchId } = useManagedBranch()
  const search = useSearch({ from: BESTSELLERS_ROUTE_ID })
  const span = (today: string) => ({ from: search.from ?? today, to: search.to ?? today })
  return (
    <ListPage title={() => t('stats.allItemsTitle')} back={(today) => viewOf(search.tab ?? 'range', span(today))}>
      {(main, today) => <Bestsellers restaurantId={restaurantId} branchId={branchId} span={span(today)} main={main} />}
    </ListPage>
  )
}

function Bestsellers({ restaurantId, branchId, span, main }: { restaurantId: string; branchId: string; span: { from: string; to: string }; main: Language }) {
  const query = useQuery(bestsellersQuery(restaurantId, branchId, span))
  return (
    <>
      <SpanLabel span={span} />
      <Loadable query={query}>
        {query.data && (
          <Card>
            <ItemList categories={query.data.categories} main={main} decimals={minorUnits(query.data.currency)} />
          </Card>
        )}
      </Loadable>
    </>
  )
}

/** The orders of one working date by number, every status or one, a page at a time (the PRD's by-date link). */
export function OrdersOfDatePage() {
  const t = useT()
  const language = useLanguage()
  const { restaurantId, branchId } = useManagedBranch()
  const search = useSearch({ from: ORDERS_ROUTE_ID })
  return (
    <ListPage
      title={(today) => t('stats.ordersOfDateTitle', { date: formatDate(search.date ?? today, language) })}
      back={(today) => ({ tab: 'date', date: search.date ?? today })}
    >
      {(_, today) => <OrdersOfDate restaurantId={restaurantId} branchId={branchId} date={search.date ?? today} status={search.status} page={search.page} />}
    </ListPage>
  )
}

function OrdersOfDate({ restaurantId, branchId, date, status, page }: { restaurantId: string; branchId: string; date: string; status?: OrderStatus | undefined; page: number }) {
  const t = useT()
  const { restaurantSlug, branchSlug } = useManagedBranch()
  const navigate = useNavigate()
  const query = useQuery(ordersOfDateQuery(restaurantId, branchId, date, page, status))
  // The filter lives in the URL (page 1 again), so paging and the back button keep it.
  const params = { restaurantSlug, branchSlug }
  const search = (status: OrderStatus | undefined, page: number) => ({ date, ...(status ? { status } : {}), page })
  return (
    <>
      <Field label={t('stats.status')} className="w-44">
        <Select value={status ?? ''} onChange={(event) => void navigate({ to: ORDERS_OF_DATE_PATH, params, search: search(parseOrderStatus(event.target.value), 1) })}>
          <option value="">{t('stats.allStatuses')}</option>
          {ORDER_STATUSES.map((candidate) => (
            <option key={candidate} value={candidate}>
              {t(`track.status.${candidate}`)}
            </option>
          ))}
        </Select>
      </Field>
      <Loadable query={query}>
        {query.data && (
          <>
            {query.data.orders.length === 0 ? (
              <p role="status" className="text-muted text-sm">
                {t('stats.noOrders')}
              </p>
            ) : (
              <Card className="p-0">
                <ul className="divide-border divide-y">
                  {query.data.orders.map((order) => (
                    <li key={order.id}>
                      <OrderRow order={order} />
                    </li>
                  ))}
                </ul>
              </Card>
            )}
            <Pager
              label={t('stats.orderPages')}
              page={page}
              total={query.data.total}
              link={(next) => ({ to: ORDERS_OF_DATE_PATH, params, search: search(status, next) })}
            />
          </>
        )}
      </Loadable>
    </>
  )
}

/**
 * Number · time · seating · status · total, the whole row a link to the order — except on a
 * closed branch (2026-09-12), whose order pages are refused, so the row is plain.
 */
function OrderRow({ order }: { order: QueueOrder }) {
  const t = useT()
  const { restaurantSlug, branchSlug, branch } = useManagedBranch()
  const language = useLanguage()
  const where = whereLabel(t, order.session?.table_name)
  const cells = (
    <>
      <span className="w-12 shrink-0 font-semibold tabular-nums">#{order.number}</span>
      <span className="min-w-0 grow">
        <span className="block truncate">{where}</span>
        <span className="text-muted block text-sm">{formatTime(order.created_at, language)}</span>
      </span>
      <StatusBadge status={order.status} />
      <span className="w-24 shrink-0 text-right font-medium tabular-nums">{formatPrice(order.total, minorUnits(order.currency), language)}</span>
    </>
  )
  if (branch.deactivated_at != null) return <div className="flex items-center gap-3 px-4 py-2.5">{cells}</div>
  return (
    <Link to={ORDER_PATH} params={{ restaurantSlug, branchSlug, orderId: order.id }} className="hover:bg-accent-soft flex items-center gap-3 px-4 py-2.5">
      {cells}
    </Link>
  )
}
